import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Lock, Loader2, ArrowRight, Eye, EyeOff, AlertCircle, Shield, X } from 'lucide-react';
import logoUrbnet from '@/assets/images/UrbNet2LOGO.png';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { autenticacaoService } from '@/services/autenticacaoService';
import { useAgenteAtualStore } from '@/stores/agenteAtualStore';
import { useToast } from '@/hooks/use-toast';
import { agenteService } from '@/services/agenteService';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AgenteReprovadoScreen } from '@/components/auth/AgenteReprovadoScreen';

export default function LoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fetchAgenteAtual = useAgenteAtualStore((s) => s.fetchAgenteAtual);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [agenteReprovado, setAgenteReprovado] = useState<{ nome: string; nomeOuvidoria?: string } | null>(null);

  const handleLogout = () => {
    autenticacaoService.removerToken();
    setAgenteReprovado(null);
    navigate('/login');
  };
  const [activateUserId, setActivateUserId] = useState('');
  const [activateCode, setActivateCode] = useState('');
  const [activatePassword, setActivatePassword] = useState('');
  const [activateConfirmPassword, setActivateConfirmPassword] = useState('');
  const [isActivating, setIsActivating] = useState(false);

  // Check if should open activate modal from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldActivate = params.get('activate') === 'true';
    if (shouldActivate) {
      const email = localStorage.getItem('activateEmail') || '';
      const code = localStorage.getItem('activateCode') || '';
      setActivateUserId(email);
      setActivateCode(code);
      setShowActivateModal(true);
      // Clear URL params
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // Verificar expiração do token e atualizar se necessário
  useEffect(() => {
    const verificarEAtualizarToken = async () => {
      const tokenAcesso = localStorage.getItem('tokenAcesso');
      const refreshToken = localStorage.getItem('tokenAtualizacao');
      const dataExpiracao = localStorage.getItem('tokenExpira');

      if (!tokenAcesso || !refreshToken || !dataExpiracao) {
        return;
      }

      try {
        const expira = new Date(dataExpiracao);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0); // Zerar hora para comparar apenas datas
        const expiraData = new Date(expira);
        expiraData.setHours(0, 0, 0, 0);

        // Se o token expirou ou expira hoje, atualizar
        if (expiraData <= hoje) {
          const resultado = await autenticacaoService.atualizarToken();

          if ('success' in resultado) {
            // Buscar dados do agente atual antes de redirecionar
            const agente = await fetchAgenteAtual();
            if (agente) {
              // Verificar se o agente está reprovado
              if (agente.status === 'Reprovado') {
                const agenteReprovadoData = {
                  nome: agente.nome,
                  nomeOuvidoria: agente.ouvidoria?.nome,
                };
                setAgenteReprovado(agenteReprovadoData);
              } else {
                navigate('/dashboard');
              }
            }
            // Se falhar, mantém na tela de login para nova autenticação
          }
          // Se falhar a atualização, não fazer nada - usuário precisa fazer login novamente
        }
      } catch (e) {
        // Falha ao verificar expiração: usuário segue para login manual
      }
    };

    verificarEAtualizarToken();
  }, [fetchAgenteAtual, navigate]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Simple validation
    if (!email || !email.includes('@')) {
      setError('E-mail inválido');
      return;
    }

    if (!password) {
      setError('Senha é obrigatória');
      return;
    }

    setIsLoading(true);
    
    try {
      // Usar o serviço de autenticação real
      const resultado = await autenticacaoService.autenticar({
        id: email,
        senha: password
      });

      if ('success' in resultado) {
        // Salvar token antes de buscar o agente atual
        await autenticacaoService.salvarToken(resultado.success);

        // Buscar dados do agente atual (agenteId, ouvidoriaId, municipioId) pelo usuarioId do JWT.
        // É obrigatório que isso conclua com sucesso antes de entrar no sistema, pois todas as
        // requisições subsequentes dependem desses IDs.
        const agente = await fetchAgenteAtual();

        if (!agente) {
          autenticacaoService.removerToken();
          setError('Não foi possível carregar os dados do agente. Tente novamente.');
          toast({
            title: 'Erro ao carregar agente',
            description: 'Não foi possível identificar o agente associado a este usuário.',
            variant: 'destructive',
          });
          return;
        }

        // Verificar se o agente está reprovado
        if (agente.status === 'Reprovado') {
          const agenteReprovadoData = {
            nome: agente.nome,
            nomeOuvidoria: agente.ouvidoria?.nome,
          };
          setAgenteReprovado(agenteReprovadoData);
          return;
        }

        toast({
          title: 'Login realizado com sucesso!',
          description: `Bem-vindo, ${resultado.success.usuario.nome}!`,
        });
        navigate('/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Erro na autenticação',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
      setError('Ocorreu um erro inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    if (error) setError(null);
  };

  const handleActivateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!activateUserId || !activateCode || !activatePassword || !activateConfirmPassword) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos.',
        variant: 'destructive',
      });
      return;
    }

    if (activatePassword !== activateConfirmPassword) {
      toast({
        title: 'Senhas não conferem',
        description: 'A senha e a confirmação devem ser iguais.',
        variant: 'destructive',
      });
      return;
    }

    if (activatePassword.length < 6) {
      toast({
        title: 'Senha muito curta',
        description: 'A senha deve ter no mínimo 6 caracteres.',
        variant: 'destructive',
      });
      return;
    }

    setIsActivating(true);

    try {
      const dataSource = new RemoteDataSource(Settings.urlSecondary);

      const result = await dataSource.post({
        endpoint: `v1/usuarios/${activateUserId}/ativar-conta`,
        requiredToken: true,
        body: {
          id: activateUserId,
          codigo: activateCode,
          definirSenha: {
            senha: activatePassword,
            confirmacaoSenha: activateConfirmPassword
          }
        },
      });

      if ('failure' in result) {
        toast({
          title: 'Erro ao ativar conta',
          description: result.failure.messages.join(', ') || 'Verifique os dados e tente novamente.',
          variant: 'destructive',
        });
        setIsActivating(false);
        return;
      }

      toast({
        title: 'Conta ativada com sucesso!',
        description: 'Sua conta foi criada e ativada com sucesso.',
      });

      setShowActivateModal(false);
      setActivateUserId('');
      setActivateCode('');
      setActivatePassword('');
      setActivateConfirmPassword('');
    } catch (error) {
      toast({
        title: 'Erro ao ativar conta',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsActivating(false);
    }
  };


  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-dark via-primary to-secondary relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-2xl" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-16 object-contain"
            />
          </div>

          {/* Main Content */}
          <div className="space-y-8 max-w-md">
            <div>
              <h2 className="font-heading text-4xl font-bold leading-tight">
                Gestão inteligente
                <br />
                de denúncias urbanas
              </h2>
              <p className="mt-6 text-lg text-white/80 leading-relaxed">
                Conecte sua ouvidoria ao futuro da participação cidadã. 
                Dashboard completo, análises em tempo real e integração total.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <p className="font-heading text-3xl font-bold">24/7</p>
                <p className="text-sm text-white/70">Disponível</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-3xl font-bold">100%</p>
                <p className="text-sm text-white/70">Online</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-3xl font-bold">API</p>
                <p className="text-sm text-white/70">Integrada</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-sm text-white/50">
            <p>© 2024 UrbNet. Todos os direitos reservados.</p>
            <div className="flex gap-4">
              <Link to="/termos-uso" className="hover:text-white/70 transition-colors">Termos</Link>
              <Link to="/politica-privacidade" className="hover:text-white/70 transition-colors">Privacidade</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-14 object-contain"
            />
          </div>

          {/* Header */}
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl font-bold">Acesso ao Sistema</h2>
            <p className="text-muted-foreground mt-2">
              Entre com suas credenciais de acesso
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                E-mail
              </Label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.gov.br"
                  value={email}
                  onChange={handleInputChange(setEmail)}
                  className={cn(
                    'pl-12 h-14 text-base bg-surface-container border-border/50',
                    'focus:bg-background focus:border-primary transition-colors',
                    error && 'border-destructive'
                  )}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  Senha
                </Label>
                <button
                  type="button"
                  onClick={() => navigate('/esqueceu-senha')}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Esqueceu a senha?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={handleInputChange(setPassword)}
                  className={cn(
                    'pl-12 pr-12 h-14 text-base bg-surface-container border-border/50',
                    'focus:bg-background focus:border-primary transition-colors',
                    error && 'border-destructive'
                  )}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive animate-scale-in">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!email || !password || isLoading}
              className="w-full h-14 text-base font-medium gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Autenticando...
                </>
              ) : (
                <>
                  Acessar Dashboard
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          {/* Activate Account Button */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/autenticacao-dois-fatores?mode=activate')}
              className="text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              <Shield className="h-4 w-4" />
              Ativar conta
            </button>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Conexão segura com criptografia de ponta a ponta</span>
          </div>

        </div>
      </div>

      {/* Activate Account Modal */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowActivateModal(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-6">
              <h3 className="text-2xl font-bold text-foreground">Ativar Conta</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Insira o GUID do usuário, o código de verificação e defina sua senha.
              </p>
            </div>

            <form onSubmit={handleActivateAccount} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="activateUserId" className="text-sm font-medium">
                  GUID do Usuário *
                </Label>
                <Input
                  id="activateUserId"
                  type="text"
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  value={activateUserId}
                  onChange={(e) => setActivateUserId(e.target.value)}
                  disabled={isActivating}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="activateCode" className="text-sm font-medium">
                  Código de Verificação *
                </Label>
                <Input
                  id="activateCode"
                  type="text"
                  placeholder="123456"
                  value={activateCode}
                  onChange={(e) => setActivateCode(e.target.value)}
                  disabled={isActivating}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="activatePassword" className="text-sm font-medium">
                  Senha *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="activatePassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={activatePassword}
                    onChange={(e) => setActivatePassword(e.target.value)}
                    className="pl-10 pr-10"
                    disabled={isActivating}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="activateConfirmPassword" className="text-sm font-medium">
                  Confirmar Senha *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="activateConfirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirme sua senha"
                    value={activateConfirmPassword}
                    onChange={(e) => setActivateConfirmPassword(e.target.value)}
                    className="pl-10"
                    disabled={isActivating}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isActivating}
                className="w-full h-12"
              >
                {isActivating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Ativando...
                  </>
                ) : (
                  'Ativar Conta'
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Tela de Agente Reprovado */}
      {agenteReprovado && (
        <AgenteReprovadoScreen
          nomeAgente={agenteReprovado.nome}
          nomeOuvidoria={agenteReprovado.nomeOuvidoria}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}
